import React from "react";
import { Link } from "react-router-dom";
import { Star, Bell, ThumbsUp, MessageSquareText, TrendingUp, ShieldCheck, Search, ArrowRight } from "lucide-react";
import { MarketingLayout } from "../../components/marketing/MarketingLayout";
import { FaqAccordion } from "../../components/marketing/FaqAccordion";
import { SEO } from "../../components/seo/SEO";
import { breadcrumbSchema, faqSchema } from "../../components/seo/schema";
import { FEATURES_BY_SLUG } from "../../data/featuresData";
import { INDUSTRIES_BY_SLUG } from "../../data/industriesData";
import { trackEvent } from "../../lib/analytics";

const HERO_IMG = "/reputation-hero-satisfaction.png";

const BEHIND_THE_SCENES = [
  { icon: Bell, title: "Identify Unhappy Customers Faster", desc: "Get alerted when customer feedback indicates there may be a problem." },
  { icon: ThumbsUp, title: "Encourage More Positive Reviews", desc: "Automatically stay connected with satisfied customers and encourage genuine reviews." },
  { icon: MessageSquareText, title: "Communicate Across Multiple Channels", desc: "Use text messaging, email, and AI-powered customer calls to help engage customers." },
  { icon: TrendingUp, title: "Strengthen Your Star Rating", desc: "Build a larger volume of positive reviews so occasional negative feedback has less influence on your overall reputation." },
  { icon: ShieldCheck, title: "Build Customer Trust", desc: "Show prospective customers that other homeowners consistently trust and recommend your business." },
  { icon: Search, title: "Strengthen Your Local Presence", desc: "A strong, active review profile can contribute to your business's visibility and credibility in local Google searches." },
];

const Stars = ({ className = "h-5 w-5" }) => (
  <div className="flex items-center justify-center gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${className} fill-amber-400 text-amber-400`} />
    ))}
  </div>
);

export default function ReputationManagementPage() {
  const data = FEATURES_BY_SLUG["reputation-management"];
  const breadcrumb = [{ name: "Home", path: "/" }, { name: "Features", path: "/features" }, { name: data.title, path: "/features/reputation-management" }];

  return (
    <MarketingLayout breadcrumb={breadcrumb}>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        path="/features/reputation-management"
        jsonLd={[breadcrumbSchema(breadcrumb), faqSchema(data.faqs)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 h-72 w-[42rem] bg-blue-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 pt-10 pb-14 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="h-14 w-14 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mb-6">
              <Star className="h-7 w-7 text-blue-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5" data-testid="feature-h1">
              Protect Your Reputation. Improve Your Ratings. Win More Customers.
            </h1>
            <p className="text-[15.5px] text-slate-400 leading-relaxed">
              One negative review shouldn't define your business. ServiceGuru Pro's Reputation Management helps you
              build a wall of genuine 5-star reviews, so occasional negative feedback has far less impact on how
              customers see you.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
            <img src={HERO_IMG} alt="Customer holding a phone showing a 5-star review request — Your Satisfaction Is Our Reputation" className="w-full object-cover" data-testid="feature-hero-image" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* Build a Wall of Positive Reviews */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-5" data-testid="reputation-wall-headline">
          Build a Wall of Positive Reviews
        </h2>
        <p className="text-[15px] text-slate-400 leading-relaxed text-center max-w-2xl mx-auto">
          By consistently generating more genuine positive reviews, occasional negative reviews can have significantly
          less impact on your overall rating and reputation. Imagine a potential customer finding your business and
          seeing:
        </p>
        <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center" data-testid="reputation-stat-rating">
            <Stars className="h-5 w-5 mb-2" />
            <div className="text-2xl font-black text-white">4.9 Stars</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center flex flex-col items-center justify-center" data-testid="reputation-stat-count">
            <div className="text-2xl font-black text-white">Hundreds</div>
            <div className="mt-1 text-[13px] text-slate-400">of Customer Reviews</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center flex flex-col items-center justify-center" data-testid="reputation-stat-fresh">
            <div className="text-2xl font-black text-white">Every Week</div>
            <div className="mt-1 text-[13px] text-slate-400">New Positive Reviews</div>
          </div>
        </div>
        <p className="mt-8 text-[15px] text-slate-400 leading-relaxed text-center max-w-2xl mx-auto">
          That's a very different first impression than a business with only a handful of reviews and several
          negative ones prominently displayed. More positive reviews. Stronger ratings. Greater trust.
        </p>
      </section>

      {/* Better Reviews Can Help You Get Found */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 sm:p-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-testid="reputation-seo-headline">
              Better Reviews Can Help You Get Found
            </h2>
            <p className="text-[15px] text-slate-400 leading-relaxed">
              Your reviews don't just influence customers — they can also influence your visibility in local Google
              search results. Google considers review signals as part of determining local search prominence.
              Building a steady history of genuine, recent customer reviews can help strengthen your business's
              online presence while giving potential customers another reason to choose you.
            </p>
          </div>
          <div className="space-y-4">
            {[
              "More visibility can mean more people discovering your business.",
              "More trust can mean more of those people calling.",
              "More customers can mean more opportunities to grow.",
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-3 text-[14.5px] text-slate-200" data-testid={`reputation-seo-line-${i}`}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/15">
                  <ArrowRight className="h-3.5 w-3.5 text-blue-400" />
                </span>
                {line}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Behind the Scenes */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4" data-testid="reputation-behind-headline">
          Reputation Management Working Behind the Scenes
        </h2>
        <p className="text-[15px] text-slate-400 leading-relaxed text-center max-w-2xl mx-auto mb-10">
          You already have a business to run. You shouldn't have to spend every day checking reviews, chasing
          customers for feedback, or wondering whether an unhappy customer is about to post something negative.
          ServiceGuru Pro helps automate the process of staying connected with customers, gathering feedback,
          encouraging reviews, and alerting your team when a customer may need personal attention.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {BEHIND_THE_SCENES.map((b) => (
            <div key={b.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={`feature-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/15 mb-3">
                <b.icon className="h-5 w-5 text-blue-400" />
              </span>
              <h3 className="text-white font-semibold text-[15px] mb-1.5">{b.title}</h3>
              <p className="text-[13.5px] text-slate-400 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pb-16 text-center">
        <div className="rounded-3xl border border-blue-500/20 bg-gradient-to-b from-blue-600/10 to-transparent p-10 sm:p-14">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-4" data-testid="reputation-closing-headline">
            Stop Letting a Few Bad Reviews Control Your Reputation.
          </h2>
          <p className="text-[15px] text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Your technicians work hard to deliver great service. Your online reputation should reflect it. Turn more
            happy customers into positive reviews, identify unhappy customers sooner, and build the reputation your
            business deserves.
          </p>
          <div className="mt-6"><Stars className="h-6 w-6" /></div>
          <p className="mt-3 text-[14px] font-semibold text-blue-300">
            Protect Your Reputation. Build More 5-Star Reviews. Grow Your Business.
          </p>
          <Link
            to="/signup"
            data-testid="reputation-cta-signup"
            onClick={() => trackEvent("cta_click", { cta_name: "reputation_page_cta", page_path: "/features/reputation-management" })}
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-bold px-7 py-3.5 shadow-sm hover:shadow-md active:scale-95 transition-all"
          >
            Get Started With ServiceGuru Pro Reputation Management <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
        <FaqAccordion faqs={data.faqs} />
      </section>

      {/* Related links */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Related features</h3>
            <div className="flex flex-wrap gap-2">
              {data.relatedFeatures.map((slug) => {
                const rf = FEATURES_BY_SLUG[slug];
                if (!rf) return null;
                return (
                  <Link key={slug} to={`/features/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-blue-400 transition-colors">
                    {rf.title}
                  </Link>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Popular with these industries</h3>
            <div className="flex flex-wrap gap-2">
              {data.relatedIndustries.map((slug) => {
                const ri = INDUSTRIES_BY_SLUG[slug];
                if (!ri) return null;
                return (
                  <Link key={slug} to={`/industries/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-blue-400 transition-colors">
                    {ri.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
